/* eslint-disable @typescript-eslint/no-unused-vars */
import {
    createMasterPlaceholderData,
    MasterPlaceholderOptionsMap,
    PLACEHOLDER_TYPE_MAP,
} from '../data/data-factory/placeholder-factory';
import { cloneDeep } from '../utils/clone-deep';
import { record } from '../utils/recorder';
import { SetPagePropertiesMutation } from '../mutation/page/set-page-properties-mutation';
import { type IShapeData, type IMasterDataDelta, DataTypeUtils } from '../data';
import { PageMutationAPI } from './page-mutation-api';
import type {
    ICTExtendedGuide,
    ICTHeaderFooterPmlDelta,
    ICTTextListStyle,
    ICTTextListStyleDelta,
} from '@tencent/slide-sdk-pb-model';
import type { MasterDataModel } from '../model/master-data-model';
import type { ShapeDataModel } from '../model/shape-data-model';
import type { MasterPlaceholderType } from '../data/data-factory/placeholder-factory';

export enum MasterStyleKey {
    titleTextStyleList = 'titleTextStyleList',
    bodyTextStyleList = 'bodyTextStyleList',
    otherTextStyleList = 'otherTextStyleList',
}

export class MasterMutationAPI extends PageMutationAPI<MasterDataModel> {
    @record()
    public addPlaceholder(placeholderType: MasterPlaceholderType) {
        const newPlaceholderData = createMasterPlaceholderData(MasterPlaceholderOptionsMap[placeholderType]);
        this.modifyPlaceholderData(newPlaceholderData);
        this.addShapes([newPlaceholderData]);
        return this.model.store.findPlaceholderShape({ type: PLACEHOLDER_TYPE_MAP[placeholderType] });
    }

    @record()
    public updateName(themeName: string) {
        const themeModel = this.model.getTheme();
        return themeModel?.api.updateTheme({}, themeName);
    }

    /**
     * 提供的全量更新 textStyles 的接口
     * @param styleListKey
     * @param styleListVal
     * @returns
     */
    @record()
    public updateTextStyles(styleListKey: string, styleListVal: ICTTextListStyle) {
        if (!['titleStyle', 'bodyStyle', 'otherStyle'].includes(styleListKey)) return;
        this.transaction(() => {
            this.updateProps<IMasterDataDelta>({ txStyles: { [styleListKey]: null } });
            this.updateProps<IMasterDataDelta>({ txStyles: { [styleListKey]: styleListVal } });
        });
    }

    /**
     * 解除 layout 占位符和 master 某一占位符的关联关系
     *
     * 1. 找到与 master 目标占位符相关联的 layout 占位符
     * 2. 更新 layout 占位符的 继承属性
     * @param parentPlaceholder master 目标占位符
     */
    @record()
    public unlinkMatchedShapesOfPlaceholder(parentPlaceholder: ShapeDataModel) {
        this.transaction(() => {
            this.model.layoutList.forEach((layout) => {
                if (this.model.id !== layout.getMaster()?.id) return;
                for (const shape of layout.placeholders) {
                    if (!this.model.store.isMatchedPlaceholder(shape.props.nvPr.ph, parentPlaceholder.props.nvPr.ph)) {
                        return;
                    }
                    shape.api.updateTransform(cloneDeep(parentPlaceholder.getInheritedData().spPr?.xfrm));
                    shape.api.updateProps({
                        spPr: { prstGeom: { prst: parentPlaceholder.props.spPr.prstGeom?.prst } },
                    });
                }
            });
        });
    }

    /**
     * 提供的全量替换更新headerFooter的接口
     * @param hf
     */
    @record()
    public updateHeaderFooter(hf: ICTHeaderFooterPmlDelta) {
        this.transaction(() => {
            this.updateProps<IMasterDataDelta>({ hf: null });
            this.updateProps<IMasterDataDelta>({ hf });
        });
    }

    /**
     * 母版使用主题字体：仅sdk 内部使用
     * 由于 2.0 数据覆盖了母版主题字体，若需要使用主题字体，需要对数据进行还原
     */
    @record()
    public useThemeFontInTextStyle() {
        const lstStyleDelta: ICTTextListStyleDelta = Array.from({ length: 9 }, (i, index) => index + 1).reduce(
            (textStyles, curIndex) => {
                return {
                    ...textStyles,
                    [`lvl${curIndex}pPr`]: {
                        defRPr: { latin: { typeface: '+mn-lt' }, ea: { typeface: '+mn-ea' } },
                    },
                };
            },
            {},
        );
        this.updateProps<IMasterDataDelta>({
            txStyles: {
                titleStyle: { lvl1pPr: { defRPr: { latin: { typeface: '+mj-lt' }, ea: { typeface: '+mj-ea' } } } },
                bodyStyle: lstStyleDelta,
                otherStyle: lstStyleDelta,
            },
        });
    }

    @record()
    public updateSlideGuideList(slideGuideList: ICTExtendedGuide[], canUndo = false) {
        this.transaction(() => {
            const mut = new SetPagePropertiesMutation({
                pageId: this.model.id,
                pageType: DataTypeUtils.toSTPageType(this.model.type),
                masterProperties: {
                    slideGuideList: {
                        guide: slideGuideList.map((guide, index) => ({ ...guide, id: `${index + 1}` })),
                    },
                },
            });
            mut.invertible = canUndo;
            this.request([mut]);
        });
    }

    /**
     * 修正母版的占位符属性
     * @param inheritShape
     */
    private modifyPlaceholderData(shape: IShapeData) {
        const frame = shape.spPr.xfrm;
        const widthRatio = this.model.parent.props.sldSz.cx / 12192000;
        const heightRatio = this.model.parent.props.sldSz.cy / 6858000;
        if (frame?.ext?.cx) {
            frame.ext.cx = frame.ext.cx * widthRatio;
        }
        if (frame?.ext?.cy) {
            frame.ext.cy = frame.ext.cy * heightRatio;
        }
        if (frame?.off?.x) {
            frame.off.x = frame.off.x * widthRatio;
        }
        if (frame?.off?.y) {
            frame.off.y = frame.off.y * heightRatio;
        }
    }
}
